<template>
  <pn-toast
    v-if="configStatusUpdateObj.isDisplayMessage"
    closable
    temporary
    :text="configStatusUpdateObj.message"
    :appearance="configStatusUpdateObj.status"
    class="notification"
    :hide="autoHideToast"
  ></pn-toast>
  <pn-tablist :value="activeTab" @tabchange="changeTab">
    <pn-tab :value="tabs.WEB" label="Web Configs"></pn-tab>
    <pn-tab :value="tabs.IOS" label="IOS Configs"></pn-tab>
    <pn-tab :value="tabs.ANDROID" label="Android Configs"></pn-tab>
  </pn-tablist>
  <div :class="[{ spinner: showLoadingSpinner }]">
    <pn-spinner size="4" v-show="showLoadingSpinner" />
  </div>
  <section v-show="activeTab === tabs.WEB">
    <ConfigTable
      :data="webConfigs"
      :platform="PLATFORMS.WEB"
      @onDeleteAction="handleDeleteConfirmationShow"
      @onEditAction="handleEditValueModalShow"
      @onAddKey="handleAddKeyModalShow"
    />
  </section>
  <section v-show="activeTab === tabs.IOS">
    <ConfigTable
      :data="iosConfigs"
      :platform="PLATFORMS.IOS"
      @onDeleteAction="handleDeleteConfirmationShow"
      @onEditAction="handleEditValueModalShow"
      @onAddKey="handleAddKeyModalShow"
    />
  </section>
  <section v-show="activeTab === tabs.ANDROID">
    <ConfigTable
      :data="androidConfigs"
      :platform="PLATFORMS.ANDROID"
      @onDeleteAction="handleDeleteConfirmationShow"
      @onEditAction="handleEditValueModalShow"
      @onAddKey="handleAddKeyModalShow"
    />
  </section>
  <ConfirmationModal
    :showSpinner="showLoadingSpinner"
    :open="showDeleteConfirmationModal"
    @onClose="closeDeleteConfirmationModal"
    @onConfirm="deleteKey"
    heading="Delete Key"
    :description="`Are you sure you wish to delete the key ${keyToDelete}?`"
  />
  <EditRemoteConfigValue
    :showSpinner="showLoadingSpinner"
    :open="showEditValueModal"
    :config="objToEdit"
    @onClose="closeEditValueModal"
    @onConfirm="updateConfig"
  />
  <AddRemoteConfigValue
    :showSpinner="showLoadingSpinner"
    :open="showAddKeyModal"
    @onClose="closeAddKeyModal"
    @onConfirm="addConfig"
  />
</template>

<script setup lang="ts">
import { ref, onMounted, reactive } from "vue";
import ConfirmationModal from "./modals/ConfirmationModal.vue";
import EditRemoteConfigValue from "./modals/EditRemoteConfigKeyValue.vue";
import AddRemoteConfigValue from "./modals/AddRemoteKeyValue.vue";
import ConfigTable from "@/components/ConfigTable.vue";
import { useRemoteConfigStore } from "@/stores/remoteConfig";
import constant from "@/constant";
import { storeToRefs } from "pinia";
const { PLATFORMS, NOTIFICATION, ACTIONS } = constant;
const tabs = ref(PLATFORMS);

const remoteConfig = useRemoteConfigStore();
const activeTab = ref(tabs.value.WEB);
const showDeleteConfirmationModal = ref(false);
const showEditValueModal = ref(false);
const showLoadingSpinner = ref(false);
const { iosConfigs, androidConfigs, webConfigs } = storeToRefs(remoteConfig);
const initialConfigUpdateStatusObj = {
  isDisplayMessage: false,
  status: "",
  message: "",
};
const keyToDelete = ref("");
const objToEdit = ref({});

onMounted(async () => {
  try {
    showLoadingSpinner.value = true;
    await getAllConfigs();
    showLoadingSpinner.value = false;
  } catch {
    showLoadingSpinner.value = false;
    console.log("Error in retrieving config data");
  }
});

const getAllConfigs = async () => {
  try {
    await Promise.all([
      remoteConfig.getRemoteConfig(PLATFORMS.IOS),
      remoteConfig.getRemoteConfig(PLATFORMS.ANDROID),
      remoteConfig.getRemoteConfig(PLATFORMS.WEB),
    ]);
  } catch (e: any) {
    console.log("error while getting configs " + e);
  }
};

const configStatusUpdateObj = reactive({ ...initialConfigUpdateStatusObj });
const showAddKeyModal = ref(false);
const autoHideToast = ref(false);
const changeTab = (e: { detail: string }) => {
  activeTab.value = e.detail;
};

const handleDeleteConfirmationShow = (key: string) => {
  reset();
  keyToDelete.value = key;
  showDeleteConfirmationModal.value = true;
};

const closeDeleteConfirmationModal = () => {
  showDeleteConfirmationModal.value = false;
};

const deleteKey = async () => {
  let updatedConfig: Record<string, any> = {};
  showLoadingSpinner.value = true;
  try {
    switch (activeTab.value) {
      case PLATFORMS.WEB:
        updatedConfig = { ...remoteConfig.webConfigs };
        break;
      case PLATFORMS.IOS:
        updatedConfig = { ...remoteConfig.iosConfigs };
        break;
      case PLATFORMS.ANDROID:
        updatedConfig = { ...remoteConfig.androidConfigs };
        break;
    }
    if (keyToDelete.value !== "") {
      delete updatedConfig[keyToDelete.value];
      await remoteConfig.updateRemoteConfig(activeTab.value, updatedConfig);
      await getAllConfigs();
      displayStatusMessage(ACTIONS.DELETE, NOTIFICATION.SUCCESS);
    } else {
      throw new Error(" Key is null");
    }
  } catch (e: any) {
    displayStatusMessage(ACTIONS.DELETE, NOTIFICATION.ERROR);
  } finally {
    showDeleteConfirmationModal.value = false;
    showLoadingSpinner.value = false;
  }
};

const handleEditValueModalShow = (configObj: Record<string, string>) => {
  reset();
  Object.assign(objToEdit.value, configObj);
  showEditValueModal.value = true;
};

const handleAddKeyModalShow = () => {
  reset();
  showAddKeyModal.value = true;
};

const closeEditValueModal = () => {
  showEditValueModal.value = false;
};

const closeAddKeyModal = () => {
  showAddKeyModal.value = false;
};

const addConfig = async (newConfig: any) => {
  showLoadingSpinner.value = true;
  let updatedConfig = {};
  try {
    switch (activeTab.value) {
      case PLATFORMS.WEB:
        updatedConfig = { ...remoteConfig.webConfigs, ...newConfig };
        break;
      case PLATFORMS.IOS:
        updatedConfig = { ...remoteConfig.iosConfigs, ...newConfig };
        break;
      case PLATFORMS.ANDROID:
        updatedConfig = { ...remoteConfig.androidConfigs, ...newConfig };
        break;
    }
    await remoteConfig.updateRemoteConfig(activeTab.value, updatedConfig);
    await getAllConfigs();
    displayStatusMessage(ACTIONS.ADD, NOTIFICATION.SUCCESS);
  } catch (e: any) {
    displayStatusMessage(ACTIONS.ADD, NOTIFICATION.ERROR);
  } finally {
    showAddKeyModal.value = false;
    showLoadingSpinner.value = false;
  }
};

const updateConfig = async (updatedConfig: any) => {
  showLoadingSpinner.value = true;
  try {
    switch (activeTab.value) {
      case PLATFORMS.WEB:
        updatedConfig = {
          ...remoteConfig.webConfigs,
          ...{ [updatedConfig.key]: updatedConfig.value },
        };
        break;
      case PLATFORMS.IOS:
        updatedConfig = {
          ...remoteConfig.iosConfigs,
          ...{ [updatedConfig.key]: updatedConfig.value },
        };
        break;
      case PLATFORMS.ANDROID:
        updatedConfig = {
          ...remoteConfig.androidConfigs,
          ...{ [updatedConfig.key]: updatedConfig.value },
        };
        break;
    }
    await remoteConfig.updateRemoteConfig(activeTab.value, updatedConfig);
    await getAllConfigs();
    displayStatusMessage(ACTIONS.EDIT, NOTIFICATION.SUCCESS);
  } catch (e: any) {
    displayStatusMessage(ACTIONS.EDIT, NOTIFICATION.ERROR);
  } finally {
    showEditValueModal.value = false;
    showLoadingSpinner.value = false;
  }
};

const displayStatusMessage = (action: string, status: string): void => {
  configStatusUpdateObj.isDisplayMessage = true;
  configStatusUpdateObj.status = status;
  setTimeout(() => {
    autoHideToast.value = true;
  }, 4000);
  switch (action) {
    case "DELETE":
      if (status === "success") {
        configStatusUpdateObj.message = "Key Deleted Successfully";
      } else if (status == "error") {
        configStatusUpdateObj.message =
          "Unable to delete Key. Please try again";
      }
      break;
    case "EDIT":
      if (status === "success") {
        configStatusUpdateObj.message = "Updated value successfully";
      } else if (status == "error") {
        configStatusUpdateObj.message =
          "Failed to update value.Please try again";
      }
      break;
    case "ADD":
      if (status === "success") {
        configStatusUpdateObj.message = "Key added successfully";
      } else if (status == "error") {
        configStatusUpdateObj.message =
          "Failed to add new key. Please try again";
      }
      break;
  }
};

const reset = () => {
  autoHideToast.value = false;
  Object.assign(configStatusUpdateObj, initialConfigUpdateStatusObj);
  keyToDelete.value = "";
  objToEdit.value = {};
};
</script>

<style lang="scss" scoped>
.config-table {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 5rem;
}

.notification {
  position: fixed;
  bottom: 12px;
  padding: 16px;
  left: 50%;
  transform: translateX(-50%);
}

.spinner {
  width: 100%;
  height: 100%;
  position: fixed;
  left: 0;
  top: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 99;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
